import type { MantineTheme } from '@mantine/core'
import type { MIME_TYPES } from '@mantine/dropzone'
import dayjs, { type Dayjs, type ConfigType, type UnitTypeLong } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { Decimal } from 'decimal.js'
import { parsePhoneNumber } from 'react-phone-number-input'

dayjs.extend(duration)

export type FileMimeType = (typeof MIME_TYPES)[keyof typeof MIME_TYPES]

export const DATE_FORMAT = 'LL'
export const DATEPICKER_FORMAT = 'DD/MM/YYYY'
export const API_DATE_FORMAT = 'YYYY-MM-DD'

export const UtilsHelper = {
  DATEPICKER_FORMAT,
  API_DATE_FORMAT,
  capitalise: (word: string): string => {
    const trimmedWord = word.trim()

    return trimmedWord.charAt(0).toUpperCase() + trimmedWord.slice(1)
  },
  addDuration: (date: ConfigType, duration: number, unit: UnitTypeLong = 'day') => {
    return dayjs(date).add(dayjs.duration({ [unit]: duration }))
  },
  isValidDate: (date: ConfigType): boolean => {
    return dayjs(date).isValid()
  },
  toDecimal: (val: string): Decimal => {
    return new Decimal(val)
  },
  toDayjs: (date: ConfigType): Dayjs => {
    return dayjs(date)
  },
  dateDiff: (sourceDate: ConfigType, targetDate: ConfigType, unit: UnitTypeLong = 'day') => {
    return dayjs(sourceDate).diff(targetDate, unit)
  },
  isBefore: (date: ConfigType, comparedDate: ConfigType) => {
    return dayjs(date).isBefore(comparedDate)
  },
  isAfter: (date: ConfigType, comparedDate: ConfigType) => {
    return dayjs(date).isAfter(comparedDate)
  },
  toDateFormat: (date: ConfigType, format = DATE_FORMAT): string => {
    return dayjs(date).format(format)
  },
  toAPIDateFormat: (date?: ConfigType): string => {
    return dayjs(date).format(API_DATE_FORMAT)
  },
  matchesSearch: (search: string, text: string | undefined) => {
    const normalize = (str: string) =>
      str
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .trim()
    return normalize(text ?? '').includes(normalize(search))
  },
  toUpperCaseFirstLetter: (str: string) => {
    return String(str).charAt(0).toUpperCase() + String(str).slice(1)
  },
  toPhoneNumberFormat: (phoneNumber?: string) => {
    if (!phoneNumber) {
      return ''
    }

    const parsedPN = parsePhoneNumber(phoneNumber)

    return parsedPN?.formatInternational()
  },
  getDateStyle: (date: Date, theme: MantineTheme, isValid = true) => {
    return date.getDay() === 6 || date.getDay() === 0
      ? { color: isValid ? theme.colors.blue[8] : theme.colors.blue[2] }
      : {}
  },
}
